import React, { useState, useRef } from 'react';
import { TextField, Button, CircularProgress, Container, Typography, Box } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../providers/SnackBarProvider';
import { validateEmail, validatePassword, verifyCaptchaToken } from '../utils/lib';
import './styles.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';
import LanguageIcon from '@mui/icons-material/Language';

function Login() {    
  const { t, i18n } = useTranslation();

  const languageLabels = { es: 'ES', en: 'EN', pt: 'PT' };
  const languageOrder = ['es', 'en', 'pt'];
  const handleLanguageCycle = () => {
    const currentIndex = languageOrder.indexOf(i18n.language);
    const nextLang = languageOrder[(currentIndex + 1) % languageOrder.length];
    i18n.changeLanguage(nextLang);
  };
  const navigate = useNavigate();
  
  const showSnackbar = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const recaptcha = useRef();

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    email: useRef(null),
    password: useRef(null),
  });
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Fetch user info from Google People API
        const userInfoResponse = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          }
        );
        
        const userInfo = userInfoResponse.data;
        //console.log("Usuario:", userInfo);

        //verify if account exists
        const response_check = await axios.post(
          `${import.meta.env.VITE_API_URL}users?facebook_google_pre_auth_web`,
          { 
            email: userInfo.email,
            login_type: 'GOOGLE',
          }
        );
        // console.log(response_check);
        // console.log(response_check.data.status)
        // console.log(response_check.data.data.state)

        if (response_check.data.status === 'SUCCESS' && response_check.data.data.state === 'VALID') {
          navigate(`/login-choice?email=${encodeURIComponent(userInfo.email)}&pre_access_token=${encodeURIComponent(response_check.data.data.pre_access_token)}`);
        } else {
          // Create account with Google info
          const response = await axios.post(
            `${import.meta.env.VITE_API_URL}users?create_account`,
            { 
              name: userInfo.name,
              email: userInfo.email,
              login_type: 'GOOGLE',
              external_thumb_url: userInfo.picture,
            }
          );
          //console.log(response);

          navigate(`/login-choice?email=${encodeURIComponent(userInfo.email)}&pre_access_token=${encodeURIComponent(response.data.data.pre_access_token)}`);
        }
      } catch (error) {
        console.error('Error:', error);
        if (error.response) {
          showSnackbar(t('login.googleLoginError'));
        }
      }
    },
    onError: () => {
      //console.log("Login Failed");
      showSnackbar(t('login.googleLoginError'));
    },
    flow: "implicit",
  });

  const handleSubmit = async(e) => {
    e.preventDefault();

    if (!validateEmail('email', email, setErrors, refs.email) || 
        !validatePassword('password', password, setErrors, refs.password))
      return;

    const captchaValue = recaptcha.current.getValue();
    if (!captchaValue) {
      showSnackbar(t('login.captchaRequired'));
      return;
    } 
    
    const validCaptcha = verifyCaptchaToken(captchaValue);
    if (!validCaptcha) {
      showSnackbar(t('login.captchaError'));
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}users?email_pre_auth_web`,
        { email, password }
      );
      //console.log(response);
      
      switch (response.data.data.state) {
        case 'ACCOUNT_WAITING_FOR_EMAIL_CHECK':
          // navigate('/login-check-valid', { state: { email: email, pre_access_token: response.data.data.pre_access_token } });
          navigate(`/login-check-valid?email=${encodeURIComponent(email)}&pre_access_token=${encodeURIComponent(response.data.data.pre_access_token)}`);
          break;

        case 'VALID':
        case 'ACCOUNT_WAITING_FOR_VET_ASSOCIATION':
          // navigate('/login-choice', { state: { email: email, pre_access_token: response.data.data.pre_access_token } });
          navigate(`/login-choice?email=${encodeURIComponent(email)}&pre_access_token=${encodeURIComponent(response.data.data.pre_access_token)}`);
          break;
      }
    } catch (error) {
      if (error.response /*&& error.response.status == 404*/)
        showSnackbar(t('login.invalidLogin'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const captchaValue = recaptcha.current.getValue()
    if (!captchaValue) {
      showSnackbar(t('login.captchaRequired'));
      return;
    }
    googleLogin();
  };

  return (
    <Container maxWidth="sm">
      <Box className="login-container">
        <Box 
          className="login-form" 
          padding={3} 
          boxShadow={3}
          component="form"
          onSubmit={handleSubmit}>

          <Typography variant="h5" gutterBottom className="login-title">
            {t('login.title')}
          </Typography>
          <TextField
            label={t('login.email')}
            variant="outlined"
            fullWidth
            type="email"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            inputRef={refs.email}
          />
          <TextField
            label={t('login.password')}
            variant="outlined"
            fullWidth
            type="password"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            inputRef={refs.password}
          />
          
          <Box display="flex" justifyContent="center" mt={1}>
            <ReCAPTCHA 
              ref={recaptcha} 
              sitekey={import.meta.env.VITE_RECAPCHA_SITE_KEY} 
            />
          </Box>

          <Box mt={2}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? <CircularProgress size={24} /> : t('login.signIn')}
            </Button>
          </Box>

          <Box mt={2} display="flex" justifyContent="center">
            <Button
              startIcon={<GoogleIcon />}
              variant="outlined"
              onClick={handleGoogleLogin}
              fullWidth
            >
              {t('login.loginWithGoogle')}
            </Button>
          </Box>

          <Box mt={4} display="flex" flexDirection="column" alignItems="center">
            <Typography
              variant="body2"
              color="primary"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/login-create-account')}
              >
              {t('login.createAccount')}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/login-forgot-password')}
              >
              {t('login.forgotPassword')}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ cursor: 'pointer' }}
              onClick={() => window.open(`${import.meta.env.VITE_WEB_URL}privacy-policy.html`, '_blank')}
              >
                {t('login.privacyPolicy')}
            </Typography>

            <Button
              size="small"
              startIcon={<LanguageIcon />}
              onClick={handleLanguageCycle}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              {languageLabels[i18n.language] || 'ES'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;